import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { Icon } from './icons';
import { useLanguage } from './LanguageContext';

const ACCENT = {
  company: '#00bf63',
  vendor:  '#8b5cf6',
};

const inputStyle = {
  width: '100%',
  border: '1.5px solid #e2e8f0',
  borderRadius: 10,
  padding: '12px 14px',
  fontSize: 14,
  fontFamily: 'inherit',
  color: '#0f172a',
  background: '#f8fafc',
  outline: 'none',
  transition: 'border-color 0.15s, background 0.15s',
};

const ApplyModal = ({ type, onClose }) => {
  const { t } = useLanguage();
  const accent = ACCENT[type] || ACCENT.company;
  const isVendor = type === 'vendor';

  const copy = {
    pill: isVendor ? t.apply.pillVendor : t.apply.pillCompany,
    title: isVendor ? t.apply.titleVendor : t.apply.titleCompany,
    sub: isVendor ? t.apply.subVendor : t.apply.subCompany,
    nameLabel: isVendor ? t.apply.nameVendor : t.apply.nameCompany,
    namePh: isVendor ? t.apply.nameVendorPh : t.apply.nameCompanyPh,
    descLabel: isVendor ? t.apply.descVendor : t.apply.descCompany,
    descPh: isVendor ? t.apply.descVendorPh : t.apply.descCompanyPh,
  };

  const [form, setForm] = useState({
    companyName: '',
    description: '',
    contactPerson: '',
    email: '',
    phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const setField = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const companyName = form.companyName.trim();
    const description = form.description.trim();
    const contactPerson = form.contactPerson.trim();
    const email = form.email.trim().toLowerCase();
    const phone = form.phone.trim();

    if (!companyName || !description || !contactPerson || !email || !phone) {
      setError(t.apply.errorRequired);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t.apply.errorEmail);
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'applications'), {
        type,
        companyName,
        description,
        contactPerson,
        email,
        phone,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setDone(true);
    } catch (err) {
      console.error('Application submit failed:', err);
      setError(err.message || t.apply.errorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 520,
          background: 'white',
          borderRadius: 18,
          boxShadow: '0 30px 80px rgba(0,0,0,0.4)',
          overflow: 'hidden',
          fontFamily: 'Plus Jakarta Sans, sans-serif',
        }}
      >
        <div style={{
          padding: '24px 28px 20px',
          borderBottom: '1px solid #f1f5f9',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 999,
              background: accent + '18',
              color: accent,
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 10,
            }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: accent }} />
              {copy.pill}
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 6px 0', letterSpacing: '-0.01em' }}>
              {copy.title}
            </h2>
            <p style={{ fontSize: 13, color: '#64748b', margin: 0, lineHeight: 1.55 }}>
              {copy.sub}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              flexShrink: 0,
              width: 34, height: 34, borderRadius: 8,
              border: 'none', background: '#f1f5f9',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#64748b',
            }}
          >
            <Icon name="x" size={16} color="#64748b" />
          </button>
        </div>

        {done ? (
          <div style={{ padding: '40px 28px', textAlign: 'center' }}>
            <div style={{
              width: 60, height: 60, borderRadius: '50%',
              background: 'rgba(16,185,129,0.12)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 18,
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
              {t.apply.successHeading}
            </div>
            <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6, margin: '0 auto 22px', maxWidth: 360 }}>
              {t.apply.success}
            </p>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '11px 22px',
                background: '#0f172a',
                color: 'white',
                border: 'none',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 700,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >{t.apply.close}</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ padding: '20px 28px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {error && (
              <div style={{
                padding: '10px 14px',
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.20)',
                color: '#dc2626',
                borderRadius: 10,
                fontSize: 13, fontWeight: 600,
              }}>{error}</div>
            )}

            <Field label={copy.nameLabel}>
              <input
                type="text"
                value={form.companyName}
                onChange={setField('companyName')}
                placeholder={copy.namePh}
                style={inputStyle}
                required
              />
            </Field>

            <Field label={copy.descLabel}>
              <textarea
                value={form.description}
                onChange={setField('description')}
                placeholder={copy.descPh}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
                required
              />
            </Field>

            <Field label={t.apply.contact}>
              <input
                type="text"
                value={form.contactPerson}
                onChange={setField('contactPerson')}
                placeholder={t.apply.contactPh}
                style={inputStyle}
                required
              />
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label={t.apply.email}>
                <input
                  type="email"
                  value={form.email}
                  onChange={setField('email')}
                  placeholder={t.apply.emailPh}
                  style={inputStyle}
                  required
                />
              </Field>
              <Field label={t.apply.phone}>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={setField('phone')}
                  placeholder={t.apply.phonePh}
                  style={inputStyle}
                  required
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{
                marginTop: 6,
                padding: '13px 18px',
                background: accent,
                color: 'white',
                border: 'none',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 700,
                fontFamily: 'inherit',
                cursor: submitting ? 'wait' : 'pointer',
                transition: 'opacity 0.15s',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? t.apply.submitting : t.apply.submit}
            </button>

            <p style={{ fontSize: 11, color: '#94a3b8', margin: 0, textAlign: 'center', lineHeight: 1.5 }}>
              {t.apply.footerNote}
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{label}</span>
    {children}
  </label>
);

export default ApplyModal;
